import { useState, useMemo, useEffect } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Eye, FileText, CheckCircle2, XCircle, AlertTriangle, ExternalLink, Activity, History, Info, MoreHorizontal, Upload, Search, X, Mail, Pencil } from "lucide-react";
import { format } from "date-fns";
import { useQuery } from "@tanstack/react-query";
import { queryClient } from "@/lib/queryClient";
import { Link, useParams, useLocation, useSearch } from "wouter";
import { Skeleton } from "@/components/ui/skeleton";
import { PageHeader } from "@/components/page-header";
import { formatReportDate } from "@/lib/utils";
import { ReportDiagnosticsDrawer, type ReportDiagnostics } from "@/components/report-diagnostics-drawer";
import { ReportHistoryDrawer } from "@/components/report-history-drawer";
import { ReportUploadDrawer } from "@/components/report-upload-drawer";
import { ReportQuickSwitcher } from "@/components/report-quick-switcher";
import { usePageTitle } from "@/hooks/use-page-title";
import { useZoneLabels } from "@/hooks/use-zone-labels";

interface ReportLineItem {
  id: string;
  animalId: string;
  name: string;
  microchipId?: string;
  species: string;
  primaryBreed?: string;
  mixNote?: string;
  zoneCode: string;
  kennelStatusNorm?: string;
  substatusNorm?: string;
  adoptionLevelNorm?: string;
  medicalDx?: string[];
  animalEvalStatus?: string;
  animalEvalRaw?: string;
}

interface Report {
  id: string;
  location: string;
  reportDate: string;
  type: string;
  status: string;
  createdAt: string;
  filePath?: string;
  pdfStorageProvider?: string;
  diagnostics?: ReportDiagnostics;
  uploadedAt?: string;
  uploadSequence?: number;
  isCurrentForDate?: boolean;
  supersededByReportId?: string | null;
  hasNewerVersion?: boolean;
  currentVersionId?: string;
  // I9.2: Provenance tracking
  sourceEmailId?: string | null;
}

interface ReportDetailData {
  report: Report;
  animals: ReportLineItem[];
}

function StatusBadge({ status }: { status: string }) {
  const variants: Record<string, { className: string; icon: any; text: string }> = {
    AVAILABLE: { 
      className: "bg-success/10 text-success border border-success/30", 
      icon: CheckCircle2, 
      text: "Available" 
    },
    UNAVAIL: { 
      className: "bg-destructive/10 text-destructive border border-destructive/30", 
      icon: XCircle, 
      text: "Unavailable" 
    },
    EVALUATION: {
      className: "bg-warning/10 text-warning border border-warning/30",
      icon: AlertTriangle,
      text: "Evaluation"
    },
    BEHAVIOR: { 
      className: "bg-muted text-muted-foreground border border-border", 
      icon: AlertTriangle, 
      text: "Behavior" 
    },
    MEDICAL: { 
      className: "bg-muted text-muted-foreground border border-border", 
      icon: FileText, 
      text: "Medical" 
    },
    STRAY_WAIT: { 
      className: "bg-muted text-muted-foreground border border-border", 
      icon: FileText, 
      text: "Stray Wait" 
    },
  };

  const config = variants[status] || { 
    className: "bg-muted text-muted-foreground border border-border", 
    icon: FileText, 
    text: status 
  };

  return (
    <Badge className={`gap-1 ${config.className}`}>
      <config.icon className="h-3 w-3" />
      {config.text}
    </Badge>
  );
}

function AdoptionBadge({ level }: { level: string }) {
  const colors: Record<string, string> = {
    L1: "bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-400",
    L2: "bg-indigo-100 text-indigo-800 dark:bg-indigo-900/30 dark:text-indigo-400",
    L3: "bg-purple-100 text-purple-800 dark:bg-purple-900/30 dark:text-purple-400",
    L4: "bg-pink-100 text-pink-800 dark:bg-pink-900/30 dark:text-pink-400",
  };

  return (
    <Badge className={colors[level] || ""}>{level}</Badge>
  );
}

export default function ReportDetail() {
  usePageTitle("Report Detail");
  const { resolveLabel } = useZoneLabels();
  const params = useParams();
  const reportId = params.reportId;
  const [, setLocation] = useLocation();
  const search = useSearch();
  const [diagnosticsOpen, setDiagnosticsOpen] = useState(false);
  const [historyOpen, setHistoryOpen] = useState(false);

  useEffect(() => {
    if (new URLSearchParams(search).get("drawer") === "diagnostics") {
      setDiagnosticsOpen(true);
    }
  }, [search]);

  const [uploadOpen, setUploadOpen] = useState(false);

  const [searchQuery, setSearchQuery] = useState("");
  const [selectedSpecies, setSelectedSpecies] = useState<string>("all");
  const [selectedStatus, setSelectedStatus] = useState<string>("all");
  const [selectedAdoptionLevel, setSelectedAdoptionLevel] = useState<string>("all");
  const [showMedicalNotesOnly, setShowMedicalNotesOnly] = useState(false);

  const { data, isLoading, refetch } = useQuery<ReportDetailData>({
    queryKey: [`/api/reports/${reportId}/animals`],
    enabled: !!reportId,
  });

  const report = data?.report;
  const animals = data?.animals || [];

  const hasActiveFilters = searchQuery !== "" || 
    selectedSpecies !== "all" || 
    selectedStatus !== "all" || 
    selectedAdoptionLevel !== "all" ||
    showMedicalNotesOnly;

  const clearFilters = () => {
    setSearchQuery("");
    setSelectedSpecies("all");
    setSelectedStatus("all");
    setSelectedAdoptionLevel("all");
    setShowMedicalNotesOnly(false);
  };

  const filteredAnimals = useMemo(() => {
    return animals.filter((animal) => {
      const searchLower = searchQuery.toLowerCase();
      const matchesSearch = !searchQuery ||
        animal.name.toLowerCase().includes(searchLower) ||
        animal.animalId.toLowerCase().includes(searchLower) ||
        (animal.primaryBreed && animal.primaryBreed.toLowerCase().includes(searchLower));

      const matchesSpecies = selectedSpecies === "all" || animal.species === selectedSpecies;

      const matchesStatus = selectedStatus === "all" || animal.kennelStatusNorm === selectedStatus;

      const matchesAdoptionLevel = selectedAdoptionLevel === "all" || (selectedAdoptionLevel === "none" ? !animal.adoptionLevelNorm : animal.adoptionLevelNorm === selectedAdoptionLevel);

      const matchesMedicalNotes = !showMedicalNotesOnly || (animal.medicalDx && animal.medicalDx.length > 0);

      return matchesSearch && matchesSpecies && matchesStatus && matchesAdoptionLevel && matchesMedicalNotes;
    });
  }, [animals, searchQuery, selectedSpecies, selectedStatus, selectedAdoptionLevel, showMedicalNotesOnly]);

  const handleUploadSuccess = (newReportId: string) => {
    queryClient.invalidateQueries({ queryKey: ["/api/reports"] });
    queryClient.invalidateQueries({ queryKey: [`/api/reports/${reportId}/animals`] });
    queryClient.invalidateQueries({ queryKey: ["/api/reports", reportId, "history"] });
    setLocation(`/reports/${newReportId}`);
  };

  if (isLoading) {
    return (
      <div className="px-6 pb-6 pt-4">
        <Skeleton className="h-20 w-full mb-6" />
        <Skeleton className="h-96 w-full" />
      </div>
    );
  }

  if (!report) {
    return (
      <div className="px-6 pb-6 pt-4">
        <Card>
          <CardContent className="p-8 text-center">
            <p className="text-muted-foreground">Report not found</p>
            <Button variant="outline" className="mt-4" asChild>
              <Link href="/reports">Back to Reports</Link>
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  const formattedDate = formatReportDate(report.reportDate);

  return (
    <div className="px-6 pb-6 pt-4">
      <PageHeader
        breadcrumb={[
          { label: "Reporting" },
          { label: "Reports", href: "/reports" },
          { label: `${report.location} - ${formattedDate}` },
        ]}
        title={`All Pets – ${report.location} – ${formattedDate}`}
        actions={
          <div className="flex items-center gap-2">
            <ReportQuickSwitcher
              triggerVariant="button"
              currentReportId={reportId}
              currentLocation={report.location}
              currentDate={report.reportDate}
            />
            {report.uploadSequence && (
              <Badge 
                variant="secondary" 
                className="text-xs"
                data-testid="badge-version"
              >
                Version {report.uploadSequence}
              </Badge>
            )}
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button
                  variant="outline"
                  size="default"
                  data-testid="button-actions"
                >
                  Actions
                  <MoreHorizontal className="h-4 w-4 ml-2" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-56">
                <DropdownMenuItem 
                  onClick={() => setHistoryOpen(true)}
                  data-testid="menu-item-history"
                >
                  <History className="h-4 w-4 mr-2" />
                  View history
                </DropdownMenuItem>
                <DropdownMenuItem 
                  onClick={() => setDiagnosticsOpen(true)}
                  data-testid="menu-item-diagnostics"
                >
                  <Activity className="h-4 w-4 mr-2" />
                  View diagnostics
                </DropdownMenuItem>
                <DropdownMenuItem 
                  onClick={() => setUploadOpen(true)}
                  data-testid="menu-item-upload"
                >
                  <Upload className="h-4 w-4 mr-2" />
                  Upload new version
                </DropdownMenuItem>
                {(report.filePath || report.pdfStorageProvider === "s3") && (
                  <DropdownMenuItem 
                    onClick={(e) => {
                      e.preventDefault();
                      const url = `/api/reports/${reportId}/file`;
                      window.open(url, "_blank", "noopener,noreferrer");
                    }}
                    data-testid="action-open-pdf"
                  >
                    <FileText className="h-4 w-4 mr-2" />
                    Open PDF
                    <ExternalLink className="h-3 w-3 ml-auto" />
                  </DropdownMenuItem>
                )}
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        }
      />

      {report.isCurrentForDate === false && report.currentVersionId && (
        <Alert className="mt-4" data-testid="alert-earlier-version">
          <Info className="h-4 w-4" />
          <AlertDescription className="flex items-center justify-between gap-2 flex-wrap">
            <span>
              You are viewing an earlier upload (Version {report.uploadSequence}).
            </span>
            {report.currentVersionId !== report.id && (
              <Button
                variant="outline"
                size="sm"
                asChild
                data-testid="button-view-current"
              >
                <Link href={`/reports/${report.currentVersionId}`}>
                  View current version
                </Link>
              </Button>
            )}
          </AlertDescription>
        </Alert>
      )}

      <div className="space-y-6 mt-6">
        <Card>
          <CardHeader>
            <CardTitle>Report Summary</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
              <div>
                <p className="text-sm text-muted-foreground">Location</p>
                <Badge variant="outline" className="mt-1">{report.location}</Badge>
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Report Date</p>
                <p className="font-medium mt-1">{formattedDate}</p>
                <p className="text-xs text-muted-foreground mt-0.5">
                  Uploaded {format(new Date(report.createdAt), "MMM dd, yyyy 'at' h:mm a")}
                </p>
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Total Animals</p>
                <p className="font-medium mt-1">{animals.length}</p>
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Status</p>
                <Badge variant="default" className="mt-1 gap-1">
                  <CheckCircle2 className="h-3 w-3" />
                  Parsed
                </Badge>
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Source</p>
                {report.sourceEmailId ? (
                  <Badge 
                    variant="outline" 
                    className="mt-1 gap-1 text-xs bg-blue-50 text-blue-700 border-blue-200 dark:bg-blue-900/20 dark:text-blue-400 dark:border-blue-800"
                    data-testid="badge-source-email"
                  >
                    <Mail className="h-3 w-3" />
                    Email
                  </Badge>
                ) : (
                  <Badge 
                    variant="outline" 
                    className="mt-1 gap-1 text-xs"
                    data-testid="badge-source-manual"
                  >
                    <Pencil className="h-3 w-3" />
                    Manual
                  </Badge>
                )}
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="space-y-4">
            <div className="flex items-center justify-between gap-4 flex-wrap">
              <CardTitle>
                Animals in Report
                {hasActiveFilters && (
                  <span className="text-sm font-normal text-muted-foreground ml-2" data-testid="text-filtered-count">
                    (showing {filteredAnimals.length} of {animals.length})
                  </span>
                )}
              </CardTitle>
            </div>
            <div className="flex flex-wrap gap-3 items-center">
              <div className="flex-1 min-w-[200px] max-w-[300px]">
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                  <Input
                    placeholder="Search animals..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className="pl-9"
                    data-testid="input-search-report-animals"
                  />
                </div>
              </div>

              <Select value={selectedSpecies} onValueChange={setSelectedSpecies}>
                <SelectTrigger className="w-[120px]" data-testid="select-species-filter">
                  <SelectValue placeholder="Species" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Species</SelectItem>
                  <SelectItem value="DOG">Dogs</SelectItem>
                  <SelectItem value="CAT">Cats</SelectItem>
                  <SelectItem value="OTHER">Other</SelectItem>
                </SelectContent>
              </Select>

              <Select value={selectedStatus} onValueChange={setSelectedStatus}>
                <SelectTrigger className="w-[140px]" data-testid="select-status-filter">
                  <SelectValue placeholder="Status" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Status</SelectItem>
                  <SelectItem value="AVAILABLE">Available</SelectItem>
                  <SelectItem value="UNAVAIL">Unavailable</SelectItem>
                  <SelectItem value="STRAY_WAIT">Stray Wait</SelectItem>
                  <SelectItem value="EVALUATION">Evaluation</SelectItem>
                </SelectContent>
              </Select>

              <Select value={selectedAdoptionLevel} onValueChange={setSelectedAdoptionLevel}>
                <SelectTrigger className="w-[140px]" data-testid="select-adoption-filter">
                  <SelectValue placeholder="Adoption Level" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Levels</SelectItem>
                  <SelectItem value="L1">Level 1</SelectItem>
                  <SelectItem value="L2">Level 2</SelectItem>
                  <SelectItem value="L3">Level 3</SelectItem>
                  <SelectItem value="L4">Level 4</SelectItem>
                  <SelectItem value="none">No Level</SelectItem>
                </SelectContent>
              </Select>

              <Button
                variant={showMedicalNotesOnly ? "default" : "outline"}
                size="sm"
                onClick={() => setShowMedicalNotesOnly(!showMedicalNotesOnly)}
                className="gap-1"
                data-testid="toggle-medical-notes"
              >
                Medical DX
              </Button>

              {hasActiveFilters && (
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={clearFilters}
                  className="gap-1"
                  data-testid="button-clear-filters"
                >
                  <X className="h-4 w-4" />
                  Clear
                </Button>
              )}
            </div>
          </CardHeader>
          <CardContent className="p-0">
            <div className="overflow-x-auto">
              <table className="w-full table-zebra">
                <thead className="border-b bg-muted/50">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-muted-foreground">
                      Name
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-muted-foreground">
                      ID
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-muted-foreground">
                      Species
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-muted-foreground">
                      Breed
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-muted-foreground">
                      Kennel
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-muted-foreground">
                      Status
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-muted-foreground">
                      Adoption
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-muted-foreground">
                      DX
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-muted-foreground">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y">
                  {filteredAnimals.map((animal) => (
                    <tr key={animal.id} className="transition-colors hover:bg-muted/50" data-testid={`row-animal-${animal.id}`}>
                      <td className="px-4 py-3 font-medium" data-testid={`text-name-${animal.id}`}>
                        {animal.name}
                      </td>
                      <td className="px-4 py-3">
                        <span className="font-mono text-sm" data-testid={`text-id-${animal.id}`}>
                          {animal.animalId}
                        </span>
                      </td>
                      <td className="px-4 py-3">
                        <Badge variant="outline">{animal.species}</Badge>
                      </td>
                      <td className="px-4 py-3 text-sm">
                        {animal.primaryBreed || "-"}
                        {animal.mixNote && <span className="text-muted-foreground"> {animal.mixNote}</span>}
                      </td>
                      <td className="px-4 py-3">
                        <Badge variant="outline">
                          {resolveLabel(animal.zoneCode)}
                        </Badge>
                      </td>
                      <td className="px-4 py-3">
                        {animal.kennelStatusNorm && <StatusBadge status={animal.kennelStatusNorm} />}
                      </td>
                      <td className="px-4 py-3">
                        {animal.adoptionLevelNorm && <AdoptionBadge level={animal.adoptionLevelNorm} />}
                      </td>
                      <td className="px-4 py-3">
                        {animal.medicalDx && animal.medicalDx.length > 0 && (
                          <Badge variant="secondary">{animal.medicalDx.length}</Badge>
                        )}
                      </td>
                      <td className="px-4 py-3">
                        <Button variant="ghost" size="sm" asChild>
                          <Link href={`/animals/${animal.animalId}`} data-testid={`button-view-${animal.id}`}>
                            <Eye className="h-4 w-4" />
                          </Link>
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              {animals.length === 0 && (
                <div className="p-8 text-center text-muted-foreground" data-testid="empty-no-animals">
                  No animals found in this report
                </div>
              )}
              {animals.length > 0 && filteredAnimals.length === 0 && (
                <div className="p-8 text-center text-muted-foreground" data-testid="empty-no-matches">
                  No animals match the current filters
                </div>
              )}
            </div>
          </CardContent>
        </Card>
      </div>

      <ReportDiagnosticsDrawer
        open={diagnosticsOpen}
        onOpenChange={setDiagnosticsOpen}
        diagnostics={report.diagnostics}
        location={report.location}
        reportDate={formattedDate}
      />

      <ReportHistoryDrawer
        reportId={report.id}
        open={historyOpen}
        onOpenChange={setHistoryOpen}
        currentReport={{
          id: report.id,
          location: report.location,
          reportDate: report.reportDate,
          uploadSequence: report.uploadSequence ?? 1,
          isCurrentForDate: report.isCurrentForDate ?? true,
        }}
      />

      <ReportUploadDrawer
        open={uploadOpen}
        onOpenChange={setUploadOpen}
        initialLocation={report.location}
        initialReportDate={report.reportDate}
        lockLocationAndDate={true}
        onSuccess={handleUploadSuccess}
      />
    </div>
  );
}
